import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Spinner, Alert, Row, Col, Badge } from 'react-bootstrap';
import { fetchActivityQuoteByIdRequest } from '../../store/actions/dataActions';
import { FiArrowLeft, FiFileText, FiCalendar, FiMapPin, FiDollarSign, FiUser, FiLayers } from 'react-icons/fi';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminActivityQuoteDetail.css';

// Helper function to get nested values
const getDeep = (obj, path, defaultValue = null) => {
    const keys = Array.isArray(path) ? path : path.split('.');
    let result = obj;
    for (const key of keys) {
        if (result == null || typeof result !== 'object') return defaultValue;
        result = result[key];
    }
    return result !== undefined && result !== null ? result : defaultValue;
};

const renderValue = (value, fallback = 'N/A') => {
    if (value === null || value === undefined || value === '') {
        return fallback;
    }
    return value;
};

const AdminActivityQuoteDetail = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { quoteId } = useParams();
    const { selectedActivityQuote } = useSelector((state) => state.data);

    useEffect(() => {
        if (quoteId) {
            dispatch(fetchActivityQuoteByIdRequest(quoteId));
        }
    }, [quoteId, dispatch]);

    const quote = selectedActivityQuote?.data?.quotes || null;
    const activityInfo = quote?.activity_info || {};
    const referenceInfo = quote?.reference_info || {};
    const subCoverage = referenceInfo?.SubCoverage || {};

    if (selectedActivityQuote.loading) {
        return (
            <div className="dashboard-page admin-activity-quote-detail-page">
                <Card>
                    <Card.Body>
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">Chargement de la quote...</p>
                        </div>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (selectedActivityQuote.error) {
        return (
            <div className="dashboard-page admin-activity-quote-detail-page">
                <Card>
                    <Card.Body>
                        <Alert variant="danger">
                            <Alert.Heading>Erreur</Alert.Heading>
                            <p>{selectedActivityQuote.error}</p>
                        </Alert>
                        <Button variant="primary" onClick={() => navigate('/activity/quotes')}>
                            <FiArrowLeft className="me-2" />
                            Retour à la liste
                        </Button>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (!quote) {
        return (
            <div className="dashboard-page admin-activity-quote-detail-page">
                <Card>
                    <Card.Body>
                        <Alert variant="warning">
                            <Alert.Heading>Quote non trouvée</Alert.Heading>
                            <p>La quote demandée n'existe pas ou n'est plus disponible.</p>
                        </Alert>
                        <Button variant="primary" onClick={() => navigate('/activity/quotes')}>
                            <FiArrowLeft className="me-2" />
                            Retour à la liste
                        </Button>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    return (
        <div className="dashboard-page admin-activity-quote-detail-page">
            <Card>
                <Card.Body>
                    <div className="page-header mb-4">
                        <div>
                            <h2 className="page-title">
                                <FiFileText className="me-2" />
                                Détails de la Quote
                            </h2>
                            <p className="text-muted mb-0">Code: {renderValue(quote.quote_code)}</p>
                        </div>
                        <Button variant="outline-secondary" onClick={() => navigate('/activity/quotes')}>
                            <FiArrowLeft className="me-2" />
                            Retour à la liste
                        </Button>
                    </div>

                    {/* Informations générales de la quote */}
                    <Card className="mb-4">
                        <Card.Header>
                            <h5 className="mb-0">
                                <FiFileText className="me-2" />
                                Informations de la Quote
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <strong>ID Quote:</strong>
                                        <div className="text-muted" style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                                            {renderValue(quote.quote_id)}
                                        </div>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <strong>Code Quote:</strong>
                                        <div className="fw-semibold">{renderValue(quote.quote_code)}</div>
                                    </div>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={4}>
                                    <div className="mb-3">
                                        <strong>Prix assurance (HT):</strong>
                                        <div className="fw-semibold text-primary">{renderValue(quote.insurance_price_excl_tsca)}</div>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="mb-3">
                                        <strong>Prix TTC:</strong>
                                        <div className="fw-semibold text-success">{renderValue(quote.price_incl_tsca)} EUR</div>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="mb-3">
                                        <strong>TSCA:</strong>
                                        <div>
                                            {renderValue(quote.tsca)}% ({renderValue(quote.tsca_amount)} EUR)
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <strong>Ratio prix Orakle (HT):</strong>
                                        <div>{renderValue(quote.orakle_price_ratio_excl_tsca)}</div>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <strong>Prime technique:</strong>
                                        <div>{renderValue(quote.technical_premium)}</div>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Informations de l'activité */}
                    <Card className="mb-4">
                        <Card.Header>
                            <h5 className="mb-0">
                                <FiCalendar className="me-2" />
                                Informations de l'Activité
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <strong>Nom:</strong>
                                        <div className="fw-semibold">{renderValue(activityInfo.name)}</div>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <strong>Code activité:</strong>
                                        <div style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                                            {renderValue(activityInfo.activity_code)}
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={4}>
                                    <div className="mb-3">
                                        <strong>Catégorie:</strong>
                                        <div>
                                            <Badge bg="info">{renderValue(activityInfo.category)}</Badge>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={4}>
                                </Col>
                                <Col md={4}>
                                    <div className="mb-3">
                                        <strong>Prix activité:</strong>
                                        <div className="fw-semibold">{renderValue(activityInfo.activity_price)}</div>
                                    </div>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <strong>Date de début:</strong>
                                        <div>
                                            {renderValue(activityInfo.activity_start_date)} à {activityInfo.activity_start_time ? activityInfo.activity_start_time.substring(0, 5) : 'N/A'}
                                        </div>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <strong>Date de fin:</strong>
                                        <div>
                                            {renderValue(activityInfo.activity_end_date)} à {activityInfo.activity_end_time ? activityInfo.activity_end_time.substring(0, 5) : 'N/A'}
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={12}>
                                    <div className="mb-3">
                                        <strong>Durée:</strong>
                                        <div>{renderValue(activityInfo.duration)}</div>
                                    </div>
                                </Col>
                            </Row>
                            {activityInfo.reservation_number && (
                                <Row>
                                    <Col md={12}>
                                        <div className="mb-3">
                                            <strong>Numéro de réservation:</strong>
                                            <div style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                                                {renderValue(activityInfo.reservation_number)}
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Adresse de l'activité */}
                    <Card className="mb-4">
                        <Card.Header>
                            <h5 className="mb-0">
                                <FiMapPin className="me-2" />
                                Localisation
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={12}>
                                    <div className="mb-3">
                                        <strong>Adresse:</strong>
                                        <div>{renderValue(activityInfo.activity_address)}</div>
                                    </div>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={4}>
                                    <div className="mb-3">
                                        <strong>Code postal:</strong>
                                        <div>{renderValue(activityInfo.activity_zip_code)}</div>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="mb-3">
                                        <strong>Ville:</strong>
                                        <div>{renderValue(activityInfo.activity_city)}</div>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="mb-3">
                                        <strong>Pays:</strong>
                                        <div>{renderValue(activityInfo.activity_country)}</div>
                                    </div>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <strong>Latitude:</strong>
                                        <div>{renderValue(activityInfo.latitude)}</div>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <strong>Longitude:</strong>
                                        <div>{renderValue(activityInfo.longitude)}</div>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Informations du fournisseur */}
                    {activityInfo.supplier_name && (
                        <Card className="mb-4">
                            <Card.Header>
                                <h5 className="mb-0">
                                    <FiUser className="me-2" />
                                    Informations du Fournisseur
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={6}>
                                        <div className="mb-3">
                                            <strong>Nom:</strong>
                                            <div>{renderValue(activityInfo.supplier_name)}</div>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="mb-3">
                                            <strong>Adresse:</strong>
                                            <div>{renderValue(activityInfo.supplier_address)}</div>
                                        </div>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={4}>
                                        <div className="mb-3">
                                            <strong>Code postal:</strong>
                                            <div>{renderValue(activityInfo.supplier_zip_code)}</div>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="mb-3">
                                            <strong>Ville:</strong>
                                            <div>{renderValue(activityInfo.supplier_city)}</div>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="mb-3">
                                            <strong>Pays:</strong>
                                            <div>{renderValue(activityInfo.supplier_country)}</div>
                                        </div>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Informations de la référence */}
                    <Card className="mb-4">
                        <Card.Header>
                            <h5 className="mb-0">
                                <FiLayers className="me-2" />
                                Informations de la Référence
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <strong>Nom:</strong>
                                        <div className="fw-semibold">{renderValue(referenceInfo.reference_name)}</div>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <strong>Code référence:</strong>
                                        <div style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                                            {renderValue(referenceInfo.reference_code)}
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={4}>
                                    <div className="mb-3">
                                        <strong>Catégorie:</strong>
                                        <div>
                                            <Badge bg="info">{renderValue(referenceInfo.reference_category)}</Badge>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={4}>
                                </Col>
                                <Col md={4}>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <strong>Trigger cumulé:</strong>
                                        <div>
                                            <Badge bg={referenceInfo.prcp_is_cumulated ? 'success' : 'secondary'}>
                                                {referenceInfo.prcp_is_cumulated ? 'Oui' : 'Non'}
                                            </Badge>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Informations de la sous-couverture */}
                    {subCoverage && Object.keys(subCoverage).length > 0 && (
                        <Card className="mb-4">
                            <Card.Header>
                                <h5 className="mb-0">
                                    <FiLayers className="me-2" />
                                    Informations de la Sous-couverture
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={6}>
                                        <div className="mb-3">
                                            <strong>Code couverture:</strong>
                                            <div style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                                                {renderValue(subCoverage.coverage_code)}
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="mb-3">
                                            <strong>Type de déclencheur:</strong>
                                            <div>
                                                <Badge bg="info">{renderValue(subCoverage.trigger_type)}</Badge>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <div className="mb-3">
                                            <strong>Période:</strong>
                                            <div>
                                                Du {renderValue(subCoverage.sub_coverage_start_date)} au {renderValue(subCoverage.sub_coverage_end_date)}
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="mb-3">
                                            <strong>Unité:</strong>
                                            <div>{renderValue(subCoverage.unit)}</div>
                                        </div>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={4}>
                                        <div className="mb-3">
                                            <strong>Trigger low:</strong>
                                            <div>{renderValue(subCoverage.trigger_low)}</div>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="mb-3">
                                            <strong>Trigger sup:</strong>
                                            <div>{renderValue(subCoverage.trigger_sup)}</div>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="mb-3">
                                            <strong>Source de données:</strong>
                                            <div>{renderValue(subCoverage.data_source)}</div>
                                        </div>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={4}>
                                        <div className="mb-3">
                                            <strong>Timing hour:</strong>
                                            <div>{renderValue(subCoverage.timing_hour)}</div>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="mb-3">
                                            <strong>Timing day:</strong>
                                            <div>{renderValue(subCoverage.timing_day)}</div>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="mb-3">
                                            <strong>Observation timing hour:</strong>
                                            <div>{renderValue(subCoverage.observation_timing_hour)}</div>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="mb-3">
                                            <strong>Observation timing day:</strong>
                                            <div>{renderValue(subCoverage.observation_timing_day)}</div>
                                        </div>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <div className="mb-3">
                                            <strong>Ratio SIV maintenu:</strong>
                                            <div>{renderValue(subCoverage.siv_ratio_maintained)}%</div>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="mb-3">
                                            <strong>Ratio SIV annulé:</strong>
                                            <div>{renderValue(subCoverage.siv_ratio_cancelled)}%</div>
                                        </div>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <div className="mb-3">
                                            <strong>Ratio prix Orakle (HT):</strong>
                                            <div>{renderValue(subCoverage.orakle_price_ratio_excl_tsca)}</div>
                                        </div>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminActivityQuoteDetail;
